/**
 * Contador de cuánto MUERDE la ventana de settle en uso real.
 *
 * Por qué existe: `MIC_SETTLE_MS` calla el mic 300 ms después de que el coach
 * deja de sonar, y eso se come el arranque de quien contesta rápido. Se midió
 * mucho CUÁNTO eco sobrevive y nunca CADA CUÁNTO muerde — que es el número que
 * decide si vale la pena seguir tocando esta defensa. Sólo muerde a quien
 * contesta en menos de ~0,3 s. Ojo con atribuirle cualquier arranque cortado:
 * el de la card #304 («My wife's name» → «My name») y el de la sesión
 * `18fc9fe2` venían del noise gate del worklet, que se comía la ventana que lo
 * abría (lo arregló su pre-roll, findings #124/#126), no de esta ventana.
 *
 * La señal: un bloque que el settle descarta y que trae muestras DISTINTAS DE
 * CERO. El noise gate del worklet ya pone ceros durante el silencio, así que
 * un descarte con muestras no-cero significa que ahí había alguien hablando
 * (o entró eco fuerte). Ver `pcm-worklet.js`.
 *
 * Es diagnóstico puro: no cambia una sola decisión del camino del audio.
 */

/**
 * Formato del PCM que el worklet le pasa al main thread: Int16 mono a 16 kHz
 * (`MicCapture.start()` → `processorOptions: { targetRate: 16000 }`).
 *
 * Acá se convierten bytes en milisegundos, así que si el worklet cambiara de
 * rate y esto no, los ms reportados serían falsos sin que nada se queje. Por
 * eso hay un tripwire que lee el literal de `MicCapture.ts` y se pone en rojo
 * si las dos puntas se separan — `tests/micSettleCounter.test.ts`.
 */
export const MIC_PCM_SAMPLE_RATE = 16_000;

/**
 * Paso del barrido de "¿este bloque trae algo?", en muestras. 16 = 1 ms.
 *
 * Por qué no se recorren las 4.000 muestras: esto corre en el camino del
 * audio, por bloque. Por qué el paso NO pierde audio: el gate del worklet
 * decide abrir/cerrar una vez por ventana de RMS de 50 ms (800 muestras), así
 * que un tramo con el gate abierto dura 800 muestras o más y el barrido le
 * pega 50 veces. El único tramo que el paso podría saltear es una cola de
 * menos de 1 ms al final del bloque, y esa la mira aparte `hasNonZeroPcm`.
 */
const NONZERO_SCAN_STRIDE = 16;

/** Cuántos ms de audio trae un bloque de PCM. */
export function pcmDurationMs(chunk: ArrayBuffer): number {
  return ((chunk.byteLength >> 1) * 1000) / MIC_PCM_SAMPLE_RATE;
}

/**
 * ¿El bloque trae alguna muestra distinta de cero? Barrido con paso (ver
 * `NONZERO_SCAN_STRIDE`) + la última muestra, que cae fuera del paso.
 */
export function hasNonZeroPcm(chunk: ArrayBuffer): boolean {
  const total = chunk.byteLength >> 1;
  if (total === 0) return false;
  const samples = new Int16Array(chunk, 0, total);
  for (let i = 0; i < total; i += NONZERO_SCAN_STRIDE) {
    if (samples[i] !== 0) return true;
  }
  return samples[total - 1] !== 0;
}

/**
 * Lo que se acumula durante una sesión de voz. Cumulativo y monótono: cada
 * reporte que sale al server es el estado completo, no un delta, así que un
 * mensaje perdido no descuadra la cuenta (gana el último).
 */
export interface SettleDropCounter {
  /** Turnos del coach cerrados. Es el denominador de "X de cada 100 turnos". */
  turns: number;
  /** Turnos en los que el settle descartó al menos un bloque CON audio. */
  bittenTurns: number;
  /** Bloques descartados con audio no-cero (suelen ser 1 o 2 por turno). */
  chunks: number;
  /** Milisegundos de audio no-cero que el settle descartó. */
  droppedMs: number;
  /** ¿La ventana viva ya mordió? Lo consume el próximo `closeSettleWindow`. */
  bitten: boolean;
}

export function createSettleDropCounter(): SettleDropCounter {
  return { turns: 0, bittenTurns: 0, chunks: 0, droppedMs: 0, bitten: false };
}

/**
 * Camino CALIENTE: lo llama el gate de settle por cada bloque que descarta.
 * Un bloque de puros ceros no cuenta — es el silencio que el noise gate ya
 * zereó, no habla perdida.
 */
export function countSettleDrop(counter: SettleDropCounter, chunk: ArrayBuffer): void {
  if (!hasNonZeroPcm(chunk)) return;
  counter.chunks += 1;
  counter.droppedMs += pcmDurationMs(chunk);
  counter.bitten = true;
}

/**
 * Camino FRÍO: cierra el turno del coach y consume la marca de "mordió".
 *
 * Se llama en `turn-end`, que es el arranque de un turno nuevo: para ese
 * momento la ventana de settle del turno ANTERIOR ya venció hace rato (pasó un
 * turno entero del usuario más la respuesta del coach). O sea que el reporte
 * va siempre un turno atrasado respecto de la ventana viva — y la ÚLTIMA
 * ventana de la sesión nunca se reporta. Es a propósito: numerador y
 * denominador se cuentan en el mismo instante, así que la proporción que se
 * lee es exacta aunque le falte el último turno.
 */
export function closeSettleWindow(counter: SettleDropCounter): void {
  counter.turns += 1;
  if (counter.bitten) {
    counter.bittenTurns += 1;
    counter.bitten = false;
  }
}
